import { ClientAccountTypeEnum, SignInProviderEnum, UserEnum } from "@/__generated__/graphql";
import { usePathname } from "@/components/hooks/use-pathname";
import { useAuth } from "@/components/shared/auth-provider";
import { AD_TRACKING_COOKIE_KEY } from "@/constants";
import { getCookie } from "@/lib/cookie";
import {
  createUnitNumber,
  ensurePhoneNumberWithPlus,
  sanitizeOptionalText,
} from "@/lib/helpers/string";
import { sendAnalyticData } from "@/lib/monitoring/analytics";
import { PosthogAnalytics } from "@/lib/monitoring/posthog";
import { storage } from "@/lib/storage";
import { useAuthState } from "@/store/auth";
import { useSignUpStore } from "@/store/auth/signUp";

export const useSignUp = () => {
  const { login } = useAuth();
  const pathname = usePathname();
  const {
    data: { signUpFormReturn, addressFormReturn },
    showModal,
    setUserInfo,
  } = useAuthState();

  const { loading: signUpLoading, fetch: signUp, error: signUpError } = useSignUpStore();

  const onSignUp = () => {
    if (!signUpFormReturn || !addressFormReturn) return;

    const { email, firstName, lastName, phoneNumber } = signUpFormReturn.getValues();
    const { label, postalCode, fullAddress, apartmentNumber, unitFloor } =
      addressFormReturn.getValues();

    const phone = ensurePhoneNumberWithPlus(phoneNumber);

    signUp(
      {
        requestPayload: {
          input: {
            accountType: ClientAccountTypeEnum.Individual,
            email,
            firstName,
            lastName,
            phoneNumber: phone,
            primaryAddress: {
              label: sanitizeOptionalText(label),
              fullAddress: fullAddress,
              postalCode,
              unitNumber: createUnitNumber(unitFloor, apartmentNumber),
              primary: true,
            },
            primaryContact: {
              firstName,
              lastName,
              email: [email],
              phoneNumber: [phone],
              primary: true,
            },
            provider: SignInProviderEnum.PhoneNumber,
            type: UserEnum.Client,
            adTrackingKey: getCookie(AD_TRACKING_COOKIE_KEY) ?? undefined,
          },
        },
      },
      { selfHandleError: true },
    ).then(({ data }) => {
      if (data?.userInfo) {
        setUserInfo(data.userInfo);
        login(data.loginArgs);
        sendAnalyticData({
          name: "user_sign_up",
          values: {
            method: SignInProviderEnum.PhoneNumber,
            path: pathname,
          },
        });
        storage.setItem("ONBOARDING", "true");
        showModal();
      }
    });
    PosthogAnalytics.track("user_sign_up_done");
  };

  return {
    onSignUp,
    signUpError,
    signUpLoading,
  };
};
